import React, { useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { GAPFAQSection } from '@/components/sections/GAPFAQSection';
import { ClaimForm } from '@/forms/ClaimForm';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertCircle, FileText, Scale, CheckCircle, ArrowRight, TrendingDown, Users, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';

const GAPInsuranceClaimsPage = () => {
  // Handle scroll behavior on page load
  useEffect(() => {
    if (window.location.hash) {
      // If there's a hash, scroll to that element after a short delay
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const gapQuestions = [
    {
      id: 'bought_gap',
      question: 'Did you buy GAP insurance when you purchased or financed a car?',
      options: [
        { label: 'Yes, I purchased GAP insurance at a dealership', value: 'yes_dealer' },
        { label: 'Yes, I bought it separately online', value: 'yes_online' },
        { label: 'I\'m not sure if I had GAP insurance', value: 'unsure' },
        { label: 'No, I didn\'t have GAP insurance', value: 'no' }
      ]
    },
    {
      id: 'told_commission',
      question: 'Were you told how much commission the dealer was earning from selling you GAP insurance?',
      options: [
        { label: 'Yes, they disclosed the commission', value: 'yes' },
        { label: 'No, commission was never mentioned', value: 'no' },
        { label: 'I don\'t remember being told about commission', value: 'unsure' }
      ]
    },
    {
      id: 'told_alternatives',
      question: 'Were you told you could buy the same GAP cover elsewhere — often for a fraction of the price?',
      options: [
        { label: 'Yes, I was told I could shop around', value: 'yes' },
        { label: 'No, I wasn\'t told about cheaper alternatives', value: 'no' },
        { label: 'I don\'t remember being told', value: 'unsure' }
      ]
    },
    {
      id: 'felt_pressure',
      question: 'Did you feel pressured to buy GAP insurance at the point of sale?',
      options: [
        { label: 'Yes, I felt pressured or rushed', value: 'yes' },
        { label: 'Somewhat — it was presented as essential', value: 'somewhat' },
        { label: 'No, I had time to consider', value: 'no' },
        { label: 'I don\'t remember', value: 'unsure' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-24 lg:py-32 bg-primary overflow-hidden">
          <div className="absolute inset-0 hero-pattern opacity-20" />
          <div className="section-container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  SRA Regulated Law Firm
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6">
                Mis-Sold GAP Insurance Claims
              </h1>
              <p className="text-lg sm:text-xl text-primary-foreground/80 leading-relaxed mb-4">
                <strong>Dealers were earning up to 70% of your GAP premium in hidden commission.</strong>
              </p>
              <p className="text-base text-primary-foreground/70 leading-relaxed mb-8">
                The FCA has acted. 80% of providers have suspended sales. Millions of consumers 
                may be owed compensation for GAP insurance that was sold without proper disclosure.
              </p>
              <Button variant="gold" size="xl" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                  Check if You Have a Claim
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Key Stats Section */}
        <section className="py-12 bg-background border-b border-border">
          <div className="section-container">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-accent mb-2">70%</div>
                <p className="text-muted-foreground">Of premiums went to dealers as hidden commission</p>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-accent mb-2">6%</div>
                <p className="text-muted-foreground">Of premiums were actually paid out in claims</p>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-accent mb-2">80%</div>
                <p className="text-muted-foreground">Of providers suspended sales after FCA action</p>
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground/60 mt-6">
              Source: <a href="https://www.fca.org.uk/publications/market-studies/ms22-1-gap-insurance-market-study" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">FCA GAP Insurance Market Study (MS22/1.3), 2024</a>
            </p>
          </div>
        </section>

        {/* The Scandal Explained */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                  The GAP Insurance Scandal
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                  What They Didn't Tell You
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  GAP insurance itself isn't the problem — it's a legitimate product that covers 
                  the difference between your car's value and what you owe on finance. <strong>The 
                  scandal is how it was sold.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  For every £300 you paid for GAP cover at a dealership, the dealer may have 
                  pocketed £210 in commission — <strong>and they never told you.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Worse still, the same cover was often available online for under £100. You weren't 
                  told that either. The FCA has now confirmed these practices were widespread and 
                  did not provide "fair value" to consumers.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="/gap-insurance/what-is-gap-insurance" className="text-accent hover:underline text-sm font-medium">
                    Read the full story →
                  </a>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=600&h=400&fit=crop"
                  alt="Car dealership finance office where GAP insurance was often mis-sold"
                  className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* The PPI Parallel */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block text-sm font-semibold text-gold uppercase tracking-wider mb-4">
                History Repeating
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                The PPI Scandal — Round Two
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Hidden commission. Pressure selling. Failure to disclose alternatives. 
                Sound familiar? GAP insurance followed the same playbook as PPI.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <Percent className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    Hidden Commission
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Commission rates varied, with some dealers receiving a significant portion 
                    of your premium. This was often not disclosed, creating an unfair relationship.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-4">
                    <AlertCircle className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    No Alternatives Given
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    You weren't told you could buy the same cover elsewhere for a 
                    fraction of the price. Dealers had no incentive to tell you.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <TrendingDown className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    Poor Value
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Regulators have raised concerns about low claim payout rates in the GAP 
                    insurance market, suggesting the product may have failed to deliver value.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-4">
                With PPI, millions of valid claims were never made. <strong>Don't make the same mistake.</strong>
              </p>
              <a href="/gap-insurance/fca-action-on-gap-insurance" className="text-accent hover:underline text-sm font-medium">
                Read about FCA action →
              </a>
            </div>
          </div>
        </section>

        {/* Claim Form Section */}
        <section id="claim-form" className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Your Assessment
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Check if Your GAP Insurance Was Mis-Sold
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Answer a few questions about how your GAP insurance was sold. This takes a few minutes to start your claim.
              </p>
            </div>

            <ClaimForm claimType="gap" questions={gapQuestions} />
          </div>
        </section>

        {/* Why Blue Lion Law */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Choose Blue Lion Law?
              </h2>
              <p className="text-muted-foreground">
                We're a regulated law firm, not a claims management company. That means higher 
                standards, proper legal expertise, and your interests always come first.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border-border text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">SRA Regulated</h3>
                  <p className="text-sm text-muted-foreground">Authorised by the Solicitors Regulation Authority (No. 815166)</p>
                </CardContent>
              </Card>

              <Card className="border-border text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">No Win, No Fee</h3>
                  <p className="text-sm text-muted-foreground">You pay nothing unless your claim succeeds</p>
                </CardContent>
              </Card>

              <Card className="border-border text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Scale className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">Legal Expertise</h3>
                  <p className="text-sm text-muted-foreground">Proper legal assessment of your individual case</p>
                </CardContent>
              </Card>

              <Card className="border-border text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">GAP + PCP Claims</h3>
                  <p className="text-sm text-muted-foreground">We can assess both claims together</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Related Content */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-8 text-center">
              Learn More About the GAP Insurance Scandal
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <a href="/gap-insurance/what-is-gap-insurance" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">What Is GAP Insurance — and How Was It Mis-Sold?</h3>
                <p className="text-sm text-muted-foreground">The full story of hidden commission and industry greed.</p>
              </a>
              <a href="/gap-insurance/how-gap-is-sold-with-pcp" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">GAP Insurance + PCP: A Double Scandal</h3>
                <p className="text-sm text-muted-foreground">How consumers were hit twice with hidden commission.</p>
              </a>
              <a href="/pcp-car-finance-claims" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">PCP Car Finance Claims</h3>
                <p className="text-sm text-muted-foreground">Your car finance may also have been mis-sold.</p>
              </a>
            </div>
          </div>
        </section>

        {/* GAP Insurance FAQ Section */}
        <GAPFAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default GAPInsuranceClaimsPage;
